import { useCallback, useEffect, useRef, useState } from 'react'

type AnyRecord = Record<string, unknown>

export type HealthProbeSummary = {
  ok: boolean
  status: number | null
  duration_ms: number
  error?: string | null
  url?: string
  checkedAt?: string
  details?: AnyRecord
}

export type GatewayHealth = {
  ok: boolean
  version?: string
  ports: unknown
  collisions: unknown[]
  recent_errors: unknown[]
}

export type ServiceHealth = {
  ok: boolean
  live_ok: boolean
  ready_ok: boolean
  db_ok: boolean
  status?: string
  database?: string
  prisma?: string
  version?: string
  health: HealthProbeSummary | null
  ready: HealthProbeSummary | null
}

export type ResourceHealth = {
  ok: boolean
  status: number | null
  duration_ms: number
  error?: string | null
  url?: string
  checkedAt?: string
  details?: AnyRecord
}

export type HealthSnapshot = {
  ok: boolean
  requestId: string | null
  timestamp: string | null
  gateway: GatewayHealth
  users: ServiceHealth
  lineage: ServiceHealth
  cache: ResourceHealth
  resources: Record<string, ResourceHealth>
  probes: Record<string, HealthProbeSummary>
  raw: unknown
}

function createEmptyService(): ServiceHealth {
  return {
    ok: false,
    live_ok: false,
    ready_ok: false,
    db_ok: false,
    status: undefined,
    database: undefined,
    prisma: undefined,
    version: undefined,
    health: null,
    ready: null
  }
}

function createEmptySnapshot(): HealthSnapshot {
  return {
    ok: false,
    requestId: null,
    timestamp: null,
    gateway: { ok: false, ports: null, collisions: [], recent_errors: [] },
    users: createEmptyService(),
    lineage: createEmptyService(),
    cache: { ok: false, status: null, duration_ms: 0 },
    resources: {},
    probes: {},
    raw: null
  }
}

function asRecord(value: unknown): AnyRecord | undefined {
  return typeof value === 'object' && value !== null ? (value as AnyRecord) : undefined
}

function coerceNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim().length) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function coerceString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length ? value : undefined
}

function isTruthyDatabaseStatus(value: unknown): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') {
    const normalized = value.toLowerCase()
    return normalized === 'connected' || normalized === 'ok' || normalized === 'healthy'
  }
  return false
}

function normalizeProbe(raw: unknown): HealthProbeSummary | null {
  const record = asRecord(raw)
  if (!record) return null

  const status = coerceNumber(record.status)
  const duration = coerceNumber(record.duration_ms) ?? 0
  const error =
    typeof record.error === 'string'
      ? record.error
      : record.error == null
        ? null
        : String(record.error)

  const url = coerceString(record.url)
  const checkedAt = coerceString(record.checkedAt)
  const details = asRecord(record.details)

  return {
    ok: Boolean(record.ok),
    status,
    duration_ms: duration,
    error,
    url,
    checkedAt,
    details
  }
}

function normalizeService(healthRaw: unknown, readyRaw: unknown, serviceRecord?: AnyRecord): ServiceHealth {
  const base = createEmptyService()
  const health = normalizeProbe(healthRaw)
  const ready = normalizeProbe(readyRaw)

  const healthDetails = (health?.details ?? {}) as AnyRecord
  const readyDetails = (ready?.details ?? {}) as AnyRecord

  // Check for database status in multiple places:
  // 1. Inside health.details or ready.details (legacy format)
  // 2. At the service record level (current backend format)
  const databaseRaw = healthDetails.database ?? readyDetails.database ?? serviceRecord?.database
  const dbOkRaw = serviceRecord?.db_ok
  const prismaRaw = healthDetails.prisma ?? readyDetails.prisma ?? serviceRecord?.prisma
  const statusRaw = healthDetails.status ?? readyDetails.status ?? serviceRecord?.status
  const versionRaw = healthDetails.version ?? readyDetails.version ?? serviceRecord?.version

  // Use explicit db_ok from service record if available, otherwise check database string
  const dbOk = dbOkRaw !== undefined ? Boolean(dbOkRaw) : isTruthyDatabaseStatus(databaseRaw)

  return {
    ...base,
    ok: Boolean(health?.ok && (ready ? ready.ok : true)),
    live_ok: Boolean(health?.ok),
    ready_ok: ready ? Boolean(ready.ok) : Boolean(health?.ok),
    db_ok: dbOk,
    status: coerceString(statusRaw),
    database: coerceString(databaseRaw),
    prisma: coerceString(prismaRaw),
    version: coerceString(versionRaw),
    health,
    ready
  }
}

function normalizeResources(rawResources: unknown): Record<string, ResourceHealth> {
  const resources = asRecord(rawResources)
  if (!resources) return {}

  return Object.entries(resources).reduce<Record<string, ResourceHealth>>((acc, [key, value]) => {
    const probe = normalizeProbe(value)
    if (probe) {
      acc[key] = {
        ok: probe.ok,
        status: probe.status,
        duration_ms: probe.duration_ms,
        error: probe.error,
        url: probe.url,
        checkedAt: probe.checkedAt,
        details: probe.details
      }
    }
    return acc
  }, {})
}

function normalizeResource(raw: unknown): ResourceHealth {
  const probe = normalizeProbe(raw)
  if (!probe) {
    return { ok: false, status: null, duration_ms: 0 }
  }
  return {
    ok: probe.ok,
    status: probe.status,
    duration_ms: probe.duration_ms,
    error: probe.error ?? undefined,
    url: probe.url,
    checkedAt: probe.checkedAt,
    details: probe.details
  }
}

function normalizeProbes(rawProbes: unknown): Record<string, HealthProbeSummary> {
  const probes = asRecord(rawProbes)
  if (!probes) return {}

  return Object.entries(probes).reduce<Record<string, HealthProbeSummary>>((acc, [key, value]) => {
    const probe = normalizeProbe(value)
    if (probe) {
      acc[key] = probe
    }
    return acc
  }, {})
}

function normalizeHealthResponse(raw: unknown): HealthSnapshot {
  const empty = createEmptySnapshot()
  const record = asRecord(raw)
  if (!record) {
    return empty
  }

  const gatewayRecord = asRecord(record.gateway)
  const usersRecord = asRecord(record.users)
  const lineageRecord = asRecord(record.lineage)

  return {
    ...empty,
    ok: Boolean(record.ok),
    requestId: coerceString(record.requestId) ?? null,
    timestamp: coerceString(record.timestamp) ?? null,
    gateway: {
      ok: Boolean(gatewayRecord?.ok),
      version: coerceString(gatewayRecord?.version),
      ports: gatewayRecord?.ports ?? empty.gateway.ports,
      collisions: Array.isArray(gatewayRecord?.collisions) ? gatewayRecord!.collisions : [],
      recent_errors: Array.isArray(gatewayRecord?.recent_errors) ? gatewayRecord!.recent_errors : []
    },
    users: normalizeService(usersRecord?.health, usersRecord?.ready, usersRecord),
    lineage: normalizeService(lineageRecord?.health, lineageRecord?.ready, lineageRecord),
    cache: normalizeResource(record.cache),
    resources: normalizeResources(record.resources),
    probes: normalizeProbes(record.probes),
    raw
  }
}

const HISTORY_WINDOW_MS = 24 * 60 * 60 * 1000
const MIN_HISTORY_INTERVAL_MS = 60_000

export function useHealth(intervalMs = 10000) {
  const [data, setData] = useState<HealthSnapshot>(() => createEmptySnapshot())
  const [loading, setLoading] = useState(true)
  const [lastFetchedAt, setLastFetchedAt] = useState<number | null>(null)
  const [history, setHistory] = useState<HealthSnapshot[]>([])
  const abortControllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    abortControllerRef.current = controller

    async function fetchHealth() {
      try {
        const res = await fetch('/health/full', { signal: controller.signal })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const json = await res.json()
        if (controller.signal.aborted) return
        const snapshot = normalizeHealthResponse(json)
        setData(snapshot)
        setLastFetchedAt(Date.now())

        // Save to history (keep last 1440 snapshots = 24h at 1-min intervals)
        setHistory((prev) => {
          const updated = [...prev, snapshot]
          const effectiveInterval = intervalMs > 0 ? intervalMs : MIN_HISTORY_INTERVAL_MS
          const maxHistorySize = Math.max(1, Math.ceil(HISTORY_WINDOW_MS / effectiveInterval))
          return updated.slice(-maxHistorySize)
        })
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return
        setData((prev) => (prev.raw ? prev : createEmptySnapshot()))
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }

    fetchHealth()
    if (intervalMs <= 0) {
      return () => { controller.abort() }
    }
    const t = setInterval(fetchHealth, intervalMs)
    return () => {
      controller.abort()
      clearInterval(t)
    }
  }, [intervalMs])

  const refresh = useCallback(async () => {
    abortControllerRef.current?.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller
    try {
      const res = await fetch('/health/full', { signal: controller.signal })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      if (controller.signal.aborted) return
      const snapshot = normalizeHealthResponse(json)
      setData(snapshot)
      setLastFetchedAt(Date.now())
      setHistory((prev) => {
        const updated = [...prev, snapshot]
        const effectiveInterval = intervalMs > 0 ? intervalMs : MIN_HISTORY_INTERVAL_MS
        const maxHistorySize = Math.max(1, Math.ceil(HISTORY_WINDOW_MS / effectiveInterval))
        return updated.slice(-maxHistorySize)
      })
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return
      setData((prev) => (prev.raw ? prev : createEmptySnapshot()))
    } finally {
      if (!controller.signal.aborted) setLoading(false)
    }
  }, [intervalMs])

  return { data, loading, lastFetchedAt, refresh, history }
}
